/**
 * Client mirror of `src/server/services/fireSafetyName.ts`. Runs the same
 * formatter the server will use on insert/save so the field and office
 * "Will be saved as: …" previews match what eventually lands on the
 * `assets.name` column.
 *
 * Any change to the format must be applied in BOTH files. The server is
 * authoritative — if the two diverge the client preview is the one that
 * needs to catch up.
 */
export type FireSafetyAssetType = "extinguisher" | "e_light";

const DEFAULT_LIGHT_TYPE = "emergency";

export function formatCapacity(rawValue: unknown, rawUnit: unknown): string {
    const value = rawValue == null ? "" : String(rawValue).trim();
    const unit = rawUnit == null ? "" : String(rawUnit).trim();
    if (!value || !unit) return "";
    return `${value}${unit}`;
}

export function humanizeLightType(rawLightType: unknown): string {
    const v = (rawLightType == null ? "" : String(rawLightType)).trim() || DEFAULT_LIGHT_TYPE;
    return v
        .replace(/_/g, " ")
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
}

/** Compose the canonical auto-name for a fire-safety asset.
 *
 *  The server passes the actual minted `family_seq_no`. The client
 *  preview can pass `nextSeqHint` — usually `existingCount + 1` — so
 *  the tech sees a realistic "#3"-style suffix before tapping Save. */
export function computeFireSafetyName(
    assetType: FireSafetyAssetType,
    meta: Record<string, unknown> | null | undefined,
    seq: number,
): string {
    const m = meta ?? {};
    if (assetType === "extinguisher") {
        const cap = formatCapacity(m.capacity_value, m.capacity_unit);
        const cls = m.extinguisher_class == null ? "" : String(m.extinguisher_class).trim().toUpperCase();
        return [cap, cls, "Extinguisher", `#${seq}`].filter(Boolean).join(" ");
    }
    const lt = humanizeLightType(m.light_type);
    return [lt, "Light", `#${seq}`].filter(Boolean).join(" ");
}
